import React from 'react';
import './CheckboxGroup.css';

interface CheckboxOption {
  value: string;
  label: string;
  exclusive?: boolean; // If true, selecting this clears all others
}

interface CheckboxGroupProps {
  name: string;
  label: string;
  options: CheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  onValidate?: (fieldName: string, value: string[], isRequired: boolean) => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  label,
  options,
  value = [],
  onChange,
  onValidate,
  required = false,
  error,
  disabled = false,
}) => {
  const handleChange = (optionValue: string, isExclusive: boolean = false) => {
    let newValue: string[];
    if (isExclusive) {
      // If exclusive (like "None"), clear all others and set only this
      newValue = [optionValue];
    } else {
      // Toggle the option
      if (value.includes(optionValue)) {
        newValue = value.filter(v => v !== optionValue);
      } else {
        // Remove any exclusive options first
        const exclusiveValues = options.filter(o => o.exclusive).map(o => o.value);
        const filtered = value.filter(v => !exclusiveValues.includes(v));
        newValue = [...filtered, optionValue];
      }
    }
    onChange(newValue);
    // Validate on change (real-time validation)
    if (onValidate) {
      setTimeout(() => {
        onValidate(name, newValue, required);
      }, 100);
    }
  };

  return (
    <div className={`form-question ${error ? 'has-error' : ''}`}>
      <fieldset className="checkbox-fieldset">
        <legend className="question-label">
          {label} {required && <span className="required">*</span>}
        </legend>
        <div className="checkbox-group">
          {options.map((option) => (
            <label key={option.value} className="checkbox-option">
              <input
                type="checkbox"
                name={name}
                value={option.value}
                checked={value.includes(option.value)}
                onChange={() => handleChange(option.value, option.exclusive)}
                disabled={disabled}
              />
              <span className="checkbox-label">{option.label}</span>
            </label>
          ))}
        </div>
        {error && (
          <span className="error-message" id={`${name}-error`} role="alert">
            {error}
          </span>
        )}
      </fieldset>
    </div>
  );
};

export default CheckboxGroup;

